// src/admin/pages/TenantCreditsSection.jsx — superadmin per-tenant credits block
// embedded in TenantManagementPage. Balance + $ spend + per-user usage + plan
// assignment. tenantId is the admin tenants-list id (t.id); passed explicitly to
// the credit endpoints (?tenant=) — required for superadmins and overrides any
// stale X-Tenant-Id via suppressTenantHeader inside creditsApi.
//
// PB2: the subscription belongs to the tenant's CUSTOMER, so the plan chosen here
// applies to every workspace that customer owns. Three states the operator must
// be able to tell apart, because each has a different remedy:
//   • subscription === null                   customer exists, no active plan → assign here
//   • noCustomer (409 TENANT_HAS_NO_CUSTOMER)  no customer at all → no wallet; operator
//                                             CLI `tenant-attach-customer`, nothing to do here
//   • CUSTOMER_NOT_INVOICEABLE on PUT         customer exists but cannot be billed → `missing[]`
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Square } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.jsx';
import CreditBalanceCard from '../../components/credits/CreditBalanceCard.jsx';
import CreditUsageTable from '../../components/credits/CreditUsageTable.jsx';
import CustomerBillingForm from '../../components/credits/CustomerBillingForm.jsx';
import { fetchCreditBalance, fetchCreditUsage } from '../../services/api/creditsApi.js';
import { adminListPlans, adminGetTenantSubscription, adminSetTenantSubscription } from '../adminApi.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const cardStyle = {
  background: COLORS.surface, borderRadius: RADII.control,
  border: `1px solid ${COLORS.border}`, padding: '24px', marginBottom: '24px',
};
const selectStyle = {
  padding: '10px 14px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`,
  fontSize: '14px', fontFamily: FONT_SANS, minWidth: '260px',
};
const noticeStyle = {
  marginTop: '12px', padding: '12px 16px', borderRadius: RADII.controlInner,
  background: SEMANTIC.attenzione.bg, border: `1px solid ${SEMANTIC.attenzione.fg}33`,
  color: SEMANTIC.attenzione.fg, fontSize: '13px',
};

export default function TenantCreditsSection({ tenantId }) {
  const { t } = useTranslation('admin');
  const { isSuperadmin } = useAuth();
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  // 409 TENANT_HAS_NO_CUSTOMER on GET or PUT. ⛔ Distinct from subscription===null
  // ("customer exists, no plan"): here there is no wallet at all, so the
  // assignment control is pointless and the remedy is an operator command.
  const [noCustomer, setNoCustomer] = useState(false);
  const [assignPlanId, setAssignPlanId] = useState('');
  const [savingPlan, setSavingPlan] = useState(false);
  const [planMsg, setPlanMsg] = useState('');
  // 409 CUSTOMER_NOT_INVOICEABLE → the invoice fields still missing on the
  // customer, straight off the error's top-level `missing[]`. null = no such
  // refusal outstanding; an array (possibly ['customer']) = show the checklist.
  const [missingBilling, setMissingBilling] = useState(null);

  const loadCredits = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    const [b, u] = await Promise.allSettled([
      fetchCreditBalance({ tenant: tenantId }),
      fetchCreditUsage({ tenant: tenantId }),
    ]);
    setBalance(b.status === 'fulfilled' ? b.value : null);
    setUsage(u.status === 'fulfilled' ? u.value : null);
    if (b.status === 'rejected' && u.status === 'rejected') {
      setError(b.reason?.message || t('credits.loadError'));
    }
    setLoading(false);
  }, [tenantId, t]);

  const loadSubscription = useCallback(async () => {
    if (!tenantId) return;
    setPlanMsg('');
    const [p, sub] = await Promise.allSettled([adminListPlans(), adminGetTenantSubscription(tenantId)]);
    setPlans(p.status === 'fulfilled' ? p.value : []);
    if (sub.status === 'rejected') {
      // Branch on the code, never the message.
      const isNoCustomer = sub.reason?.code === 'TENANT_HAS_NO_CUSTOMER';
      setNoCustomer(isNoCustomer);
      setSubscription(null);
      setAssignPlanId('');
      if (!isNoCustomer) setPlanMsg(sub.reason?.message || '');
      return;
    }
    const current = sub.value;
    setNoCustomer(false);
    setSubscription(current);
    setAssignPlanId(current?.plan_id || '');
  }, [tenantId]);

  useEffect(() => { loadCredits(); }, [loadCredits]);
  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  async function handleAssign() {
    if (!assignPlanId || assignPlanId === subscription?.plan_id) return;
    setSavingPlan(true);
    setPlanMsg('');
    setMissingBilling(null);
    try {
      await adminSetTenantSubscription(tenantId, assignPlanId);
      setPlanMsg(t('credits.planAssigned'));
      await Promise.all([loadSubscription(), loadCredits()]);
    } catch (err) {
      // Branch on the code, never the message.
      if (err.code === 'CUSTOMER_NOT_INVOICEABLE' && Array.isArray(err.missing) && err.missing.length) {
        // A paid plan (starter/full/pro) is refused until the customer is
        // invoiceable; `free` never trips this. An empty `missing[]` would render
        // a checklist with nothing on it, so that case keeps the server message.
        setMissingBilling(err.missing);
      } else if (err.code === 'TENANT_HAS_NO_CUSTOMER') {
        setNoCustomer(true);
      } else if (err.code === 'SUBSCRIPTION_CONFLICT') {
        // A concurrent reassignment won the race: what we showed is stale, so
        // refetch and let the operator decide whether their change still applies.
        // Never auto-retry — the other write may have been the intended one.
        await loadSubscription();
        setPlanMsg(t('credits.conflictRefetched'));
      } else {
        // TENANT_LIMIT_REACHED is reserved and not built: it lands here as a
        // plain message on purpose. Do not add a branch for it.
        setPlanMsg(err.message);
      }
    } finally {
      setSavingPlan(false);
    }
  }

  const currentPlanName = plans.find(p => p.id === subscription?.plan_id)?.display_name;
  const assignDisabled = noCustomer || savingPlan || !assignPlanId || assignPlanId === subscription?.plan_id;

  return (
    <>
      <div style={cardStyle}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>{t('credits.tenantPlan')}</h3>
        {noCustomer ? (
          <div role="alert" style={{ ...noticeStyle, marginTop: 0, marginBottom: '12px' }}>
            <strong style={{ display: 'block', marginBottom: '6px' }}>{t('credits.noCustomer.title')}</strong>
            <p style={{ margin: 0 }}>{t('credits.noCustomer.body')}</p>
          </div>
        ) : (
          <p style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: 0, marginBottom: '12px' }}>
            {subscription
              ? (
                <>
                  {t('credits.currentPlan')}{' '}
                  <strong style={{ color: COLORS.ink }}>{currentPlanName || subscription.plan_id}</strong>
                </>
              )
              : t('credits.noPlan')}
          </p>
        )}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            aria-label={t('credits.selectPlan')}
            value={assignPlanId}
            onChange={e => setAssignPlanId(e.target.value)}
            style={selectStyle}
            disabled={noCustomer}
          >
            <option value="">{t('credits.selectPlan')}</option>
            {plans.map(p => (
              <option key={p.id} value={p.id}>{p.display_name}{p.active ? '' : t('credits.inactiveSuffix')}</option>
            ))}
          </select>
          <button onClick={handleAssign} disabled={assignDisabled} style={{
            padding: '10px 16px', borderRadius: RADII.controlInner, border: 'none',
            background: assignDisabled ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
            fontSize: '13px', fontWeight: 600, cursor: assignDisabled ? 'not-allowed' : 'pointer',
            fontFamily: FONT_SANS,
          }}>
            {savingPlan ? t('credits.saving') : t('credits.assignPlan')}
          </button>
        </div>
        {/* 9.2: the period does NOT restart on every PUT — say what actually happens. */}
        {!noCustomer && (
          <p style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '10px', marginBottom: 0 }}>
            {t('credits.assignHelp')}
          </p>
        )}
        {planMsg && <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '10px' }}>{planMsg}</div>}

        {/* CUSTOMER_NOT_INVOICEABLE — what is still missing before this customer
            can be billed. The checklist itself stays read-only; the billing form
            rendered under this card (Phase 12) is where the fields get filled. */}
        {missingBilling && (
          <div role="alert" style={noticeStyle}>
            <strong style={{ display: 'block', marginBottom: '6px' }}>{t('credits.notInvoiceable.title')}</strong>
            {/* ['customer'] is not a field the operator can tick off — it means the
                tenant has NO customer row at all, which is a different (operator)
                fix, so it never renders as a to-do list. */}
            {missingBilling.includes('customer') ? (
              <p style={{ margin: 0 }}>{t('credits.notInvoiceable.noCustomer')}</p>
            ) : (
              <>
                <p style={{ margin: '0 0 8px' }}>{t('credits.notInvoiceable.body')}</p>
                <ul style={{ margin: '0 0 8px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {missingBilling.map(fieldName => (
                    <li key={fieldName} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Square size={14} aria-hidden="true" />
                      {/* Unknown field names pass through verbatim rather than
                          vanishing: the BE may add one before the FE knows it. */}
                      <span>{t(`credits.notInvoiceable.fields.${fieldName}`, { defaultValue: fieldName })}</span>
                    </li>
                  ))}
                </ul>
                <span style={{ fontSize: '11px', opacity: 0.85 }}>{t('credits.notInvoiceable.readOnly')}</span>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
        {/* canAssignPlan: the reader here IS the administrator and the subscription
            selector sits right above, so the blocked no-plan state must point at it
            instead of telling them to "contact your administrator". For a
            no-customer tenant /balance still answers (customer_id: null, has_plan
            false) and the card reads blocked; the notice above names the real fix. */}
        {/* ⛔ `showMoney` is gated EXPLICITLY, not left bare. This block is
            superadmin-only today purely because of where it is mounted
            (/admin/tenants, behind RequireSuperadmin) — and it also hosts
            CustomerBillingForm, which IS designed to be customer-admin-reachable
            elsewhere. A guarantee that depends on the mount point leaks silently
            the first time the component is reused. The backend nulls cost_usd for
            non-superadmins regardless; this is the second layer, not the only one. */}
        <CreditBalanceCard balance={balance} showMoney={isSuperadmin} canAssignPlan loading={loading} />
        {/* Phase 12: the customer's billing record, keyed on /balance's customer_id.
            A superadmin may also set country_code. Saving clears the 409 checklist
            above (its list is the same derivation as missing_billing_fields). A
            no-customer tenant has customer_id null → no form (operator fix). */}
        {balance?.customer_id && (
          <CustomerBillingForm
            customerId={balance.customer_id}
            canEditCountry
            onSaved={() => setMissingBilling(null)}
          />
        )}
        <CreditUsageTable usage={usage} showMoney={isSuperadmin} loading={loading} />
      </div>
    </>
  );
}
